import { useState } from 'react';
import {
  Card, CardContent, Grid, Stack, Button, TextField, MenuItem, Typography, Box, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
} from '@mui/material';
import { Add, AccountBalanceWallet, ReceiptLong, WarningAmber } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ListFilters, matchFilters, EMPTY_FILTERS, type FilterValue } from '../../components/ListFilters';
import { sourcingApi, type ManufacturerInvoice } from '../../api/sourcing.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string) => (s === 'paid' ? 'success' : s === 'partial' ? 'warning' : 'default');

export function ManufacturerPaymentsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [create, setCreate] = useState(false);
  const [payFor, setPayFor] = useState<ManufacturerInvoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ manufacturerId: '', invoiceNumber: '', invoiceAmount: 0, invoiceReceivedDate: '', creditDays: 45 });
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });
  const [pay, setPay] = useState({ amount: 0, paymentDate: '' });

  const manufacturers = useQuery({ queryKey: ['mfr', 'list'], queryFn: () => sourcingApi.listManufacturers({ limit: 200 }) });
  const recon = useQuery({ queryKey: ['mfr-pay', 'recon'], queryFn: () => sourcingApi.reconciliation() });
  const invoices = useQuery({ queryKey: ['mfr-pay', 'list'], queryFn: () => sourcingApi.listInvoices({ limit: 200 }) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['mfr-pay'] }); qc.invalidateQueries({ queryKey: ['mfr'] }); };

  const createMut = useMutation({
    mutationFn: () => sourcingApi.createInvoice({ ...form, manufacturerId: Number(form.manufacturerId), invoiceAmount: Number(form.invoiceAmount), creditDays: Number(form.creditDays) }),
    onSuccess: () => { setCreate(false); setError(null); setToast('Invoice recorded'); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const payMut = useMutation({
    mutationFn: () => sourcingApi.recordPayment(payFor!.id, { amount: Number(pay.amount), paymentDate: pay.paymentDate || undefined }),
    onSuccess: () => { setPayFor(null); setPay({ amount: 0, paymentDate: '' }); setToast('Payment recorded'); invalidate(); },
    onError: (e) => setToast(extractApiError(e)),
  });

  const t = recon.data?.totals;
  const ag = recon.data?.ageing ?? {};
  return (
    <>
      <PageHeader title="Manufacturer Payments" subtitle="Payment reconciliation — 45-day credit from invoice received date."
        action={hasPermission('manufacturer_payment.create') ? <Button startIcon={<Add />} variant="contained" onClick={() => setCreate(true)}>Record Invoice</Button> : undefined} />

      <ListFilters value={filters} onChange={setFilters} statusOptions={['unpaid', 'partial', 'paid']}
        show={['search', 'status', 'month', 'dateFrom', 'dateTo']} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Metric icon={<ReceiptLong />} label="Total invoiced" value={inr(t?.totalInvoiced)} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<AccountBalanceWallet />} label="Paid" value={inr(t?.totalPaid)} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<AccountBalanceWallet />} label="Outstanding" value={inr(t?.totalOutstanding)} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<WarningAmber />} label="Overdue" value={inr(t?.overdue)} /></Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}><CardContent>
        <Typography variant="subtitle2" gutterBottom>Outstanding Ageing</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {['current', '1-30', '31-60', '61-90', '90+'].map((k) => <Chip key={k} label={`${k}: ${inr(ag[k])}`} variant="outlined" />)}
        </Stack>
      </CardContent></Card>

      <Card variant="outlined"><CardContent>
        <Table size="small">
          <TableHead><TableRow><TableCell>Invoice #</TableCell><TableCell align="right">Amount</TableCell><TableCell align="right">Paid</TableCell><TableCell align="right">Outstanding</TableCell><TableCell>Received</TableCell><TableCell>Due</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {(invoices.data?.data ?? []).filter((i) => matchFilters(filters, { text: i.invoiceNumber, status: i.status, date: i.invoiceReceivedDate })).map((i) => (
              <TableRow key={i.id} hover>
                <TableCell>{i.invoiceNumber}</TableCell>
                <TableCell align="right">{inr(i.invoiceAmount)}</TableCell>
                <TableCell align="right">{inr(i.paidAmount)}</TableCell>
                <TableCell align="right">{inr(i.outstanding)}</TableCell>
                <TableCell>{i.invoiceReceivedDate}</TableCell>
                <TableCell>{i.dueDate ?? '—'}</TableCell>
                <TableCell><Chip size="small" color={statusColor(i.status)} label={i.status} /></TableCell>
                <TableCell align="right">{hasPermission('manufacturer_payment.update') && i.status !== 'paid' && <Button size="small" onClick={() => setPayFor(i)}>Pay</Button>}</TableCell>
              </TableRow>
            ))}
            {(invoices.data?.data.length ?? 0) === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" py={2} textAlign="center">No invoices.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Record invoice */}
      <Dialog open={create} onClose={() => setCreate(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record Manufacturer Invoice</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Stack spacing={1.5} mt={1}>
            <TextField select label="Manufacturer" value={form.manufacturerId} onChange={(e) => setForm((f) => ({ ...f, manufacturerId: e.target.value }))} fullWidth required>
              {(manufacturers.data?.data ?? []).map((m) => <MenuItem key={m.id} value={m.id}>{m.name} ({m.manufacturerCode})</MenuItem>)}
            </TextField>
            <TextField label="Invoice Number" value={form.invoiceNumber} onChange={(e) => setForm((f) => ({ ...f, invoiceNumber: e.target.value }))} fullWidth />
            <Stack direction="row" spacing={1}>
              <TextField label="Invoice Amount" type="number" value={form.invoiceAmount} onChange={(e) => setForm((f) => ({ ...f, invoiceAmount: Number(e.target.value) }))} fullWidth />
              <TextField label="Credit Days" type="number" value={form.creditDays} onChange={(e) => setForm((f) => ({ ...f, creditDays: Number(e.target.value) }))} sx={{ width: 140 }} />
            </Stack>
            <TextField label="Invoice Received Date" type="date" InputLabelProps={{ shrink: true }} value={form.invoiceReceivedDate} onChange={(e) => setForm((f) => ({ ...f, invoiceReceivedDate: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setCreate(false)}>Cancel</Button><Button variant="contained" disabled={!form.manufacturerId || !form.invoiceNumber || !form.invoiceReceivedDate || createMut.isPending} onClick={() => createMut.mutate()}>Record</Button></DialogActions>
      </Dialog>

      {/* Record payment */}
      <Dialog open={payFor != null} onClose={() => setPayFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Record Payment — {payFor?.invoiceNumber}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1 }}><Typography variant="body2" color="text.secondary">Outstanding: {inr(payFor?.outstanding)}</Typography></Box>
          <Stack spacing={1.5} mt={1}>
            <TextField label="Amount" type="number" value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: Number(e.target.value) }))} fullWidth />
            <TextField label="Payment Date" type="date" InputLabelProps={{ shrink: true }} value={pay.paymentDate} onChange={(e) => setPay((p) => ({ ...p, paymentDate: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setPayFor(null)}>Cancel</Button><Button variant="contained" disabled={!pay.amount || payMut.isPending} onClick={() => payMut.mutate()}>Record Payment</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', color: '#fff', borderRadius: 2, boxShadow: '0 6px 18px rgba(30,58,138,0.18)' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}><Typography variant="h6" fontWeight={800} lineHeight={1.15} noWrap>{value}</Typography><Typography variant="caption" sx={{ opacity: 0.85 }}>{label}</Typography></Box>
      </CardContent>
    </Card>
  );
}
